import type { ISavedView, ViewLayoutType } from '@featit/shared';
import { ApiService, type ApiServiceTransport } from './ApiService';

export interface CreateViewData {
  name: string;
  layout: ViewLayoutType;
  filters: Record<string, unknown>;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  isShared?: boolean;
}

export class SavedViewApiService extends ApiService {
  findAll(projectId: string): Promise<ISavedView[]> {
    return this.get(`/work-areas/${projectId}/views`);
  }

  create(projectId: string, data: CreateViewData): Promise<ISavedView> {
    return this.post(`/work-areas/${projectId}/views`, data);
  }

  update(projectId: string, viewId: string, data: Partial<CreateViewData>): Promise<ISavedView> {
    return this.patch(`/work-areas/${projectId}/views/${viewId}`, data);
  }

  /** `moveContentsOut` deletes a folder that still holds things, moving them up a level (I-1390). */
  remove(projectId: string, viewId: string, moveContentsOut = false): Promise<void> {
    return this.delete(
      `/work-areas/${projectId}/views/${viewId}${moveContentsOut ? '?contents=move-out' : ''}`,
    );
  }

  /**
   * The whole order of one folder's children, first to last — or the top level's, when
   * `parentFolderId` is null (F-0363 slice 2, AC-10/AC-12). Mirrors
   * `ProjectApiService.reorderWorkAreaDashboardPages` (F-0201). The server refuses (409) a list
   * that no longer names that level's current set.
   */
  reorder(
    projectId: string,
    viewIds: string[],
    parentFolderId: string | null = null,
  ): Promise<ISavedView[]> {
    return this.put(`/work-areas/${projectId}/views/order`, { parentFolderId, viewIds });
  }

  /**
   * Where a view or folder sits: which folder it lives under, and optionally where among that
   * folder's children, 1-based (F-0363 slice 2, AC-9/AC-10). Mirrors
   * `ProjectApiService.moveWorkAreaDashboardPage` (F-0201).
   */
  move(
    projectId: string,
    viewId: string,
    data: { parentFolderId: string | null; position?: number },
  ): Promise<ISavedView> {
    return this.put(`/work-areas/${projectId}/views/${viewId}/parent`, data);
  }

  /**
   * I-1335: where a built-in view sits (`parentFolderId`, 1-based `position`), or whether the work
   * area hides it; its placement is made on first use. Absent fields stay as they are.
   */
  placeBuiltIn(
    projectId: string,
    seedId: string,
    data: { parentFolderId?: string | null; position?: number; hidden?: boolean },
  ): Promise<ISavedView> {
    return this.put(`/work-areas/${projectId}/views/builtins/${encodeURIComponent(seedId)}`, data);
  }

  /** I-1335's Reset position: the built-in goes back to the top, in coded order, and is shown. */
  resetBuiltIn(projectId: string, seedId: string): Promise<void> {
    return this.delete(`/work-areas/${projectId}/views/builtins/${encodeURIComponent(seedId)}`);
  }

  /** An empty folder for grouping custom views (F-0363 slice 2); never a seed, never a bare view. */
  createFolder(
    projectId: string,
    data: { name: string; parentFolderId?: string | null },
  ): Promise<ISavedView> {
    return this.post(`/work-areas/${projectId}/views/folders`, data);
  }
}

export const savedViewApi = new SavedViewApiService();

export function createSavedViewApi(transport: ApiServiceTransport): SavedViewApiService {
  return new SavedViewApiService(transport);
}
